// @ts-nocheck
import React, { useRef, useState } from 'react';
import PDFViewerWithAI from '../PDFViewerWithAI';
import SessionCanvas from '../canvas/SessionCanvas';
import { apiPost } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import type { SessionCanvasHandle } from '../../types/canvas';
import './UnifiedWorkspace.css';

interface SessionInfo {
  id: string;
  pdf_fingerprint?: string;
  [key: string]: any;
}

interface UnifiedWorkspaceProps {
  pdfFile: string | null;
  originalFile: File | null;
  currentSession: SessionInfo;
  onBack: () => void;
  onPageChange: (page: number) => void;
  currentPage: number;
  existingFingerprint?: string | null;
}

/**
 * UnifiedWorkspace - 统一工作台
 * 实现了"左文右图"的 Split View，支持将 PDF 选中文本直接拖拽到 Canvas 生成 Block
 */
const UnifiedWorkspace: React.FC<UnifiedWorkspaceProps> = ({
  pdfFile,
  originalFile,
  currentSession,
  onBack,
  onPageChange,
  currentPage,
  existingFingerprint = null
}) => {
  const canvasRef = useRef<SessionCanvasHandle>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const text = e.dataTransfer.getData('text');
    if (!text || !text.trim()) return;

    const bounds = e.currentTarget.getBoundingClientRect();
    const position = {
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top
    };

    logger.log('📦 Dropped text:', text.substring(0, 20) + '...');
    logger.log('📍 Drop position:', position);

    try {
      const response = await apiPost('/api/blocks/', {
        type: 'pdf_snippet',
        session_id: currentSession.id,
        pdf_fingerprint: currentSession.pdf_fingerprint,
        content: {
          text: text,
          title: '摘录',
          source: 'drag_drop'
        },
        position: position,
        size: { width: 300, height: 200 }
      }) as any;

      if (response.success && response.block) {
        if (canvasRef.current) {
          canvasRef.current.addBlock(response.block);
          logger.log('✅ Block added to canvas via ref');
        }
      }
    } catch (err: any) {
      console.error('❌ Failed to create block from drop:', err);
      alert('创建摘录失败: ' + err.message);
    }
  };

  return (
    <div className="unified-workspace-container">
      <div className="workspace-left-panel">
        <PDFViewerWithAI
          pdfFile={pdfFile}
          originalFile={originalFile}
          onPageChange={onPageChange}
          currentPage={currentPage}
          sessionMode={true}
          currentSession={currentSession}
          compactMode={true}
          existingFingerprint={existingFingerprint}
        />
      </div>

      <div
        className={`workspace-right-panel ${isDragOver ? 'drag-over' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className="drop-hint">
            松开鼠标生成摘录卡片
          </div>
        )}

        <SessionCanvas
          ref={canvasRef}
          sessionId={currentSession.id}
          pdfFingerprint={currentSession.pdf_fingerprint}
          readOnly={false}
        />
      </div>
    </div>
  );
};

export default UnifiedWorkspace;
